import Axios, { AxiosResponse } from 'axios';
import PropTypes from 'prop-types';
import { Action } from 'redux';
import { ThunkAction, ThunkDispatch } from 'redux-thunk';
import { ConfigBase } from '../../types/config';

import { AddValueAndCareCardDTO } from '../../types/dto/AddValueAndCareCardDTO';
import { AddVehicleDTO } from '../../types/dto/AddVehicleDTO';
import { BinaryFileWithContentDTO } from '../../types/dto/BinaryFileWithContentDTO';
import { CardDetailsDTO } from '../../types/dto/CardDetailsDTO';
import { ChangePasswordDTO } from '../../types/dto/ChangePasswordDTO';
import { CustomerDataDTO } from '../../types/dto/CustomerDataDTO';
import { InvoiceGroupDTO } from '../../types/dto/InvoiceGroupDTO';
import { MaintenanceContractChartResultDTO } from '../../types/dto/MaintenanceContractChartResultDTO';
import { MaintenanceContractUpgradeChartResultDTO } from '../../types/dto/MaintenanceContractUpgradeChartResultDTO';
import { SRPMMChartResultDTO } from '../../types/dto/SRPMMChartResultDTO';
import { UpdateCustomerDTO } from '../../types/dto/UpdateCustomerDTO';
import { UpdateCustomerForSubscriptionDTO } from '../../types/dto/UpdateCustomerForSubscriptionDTO';
import { VehicleDataDTO } from '../../types/dto/VehicleDataDTO';
import { VehicleDocsDTO } from '../../types/dto/VehicleDocsDTO';
import { VehicleHistoryDTO } from '../../types/dto/VehicleHistoryDTO';
import { Utils } from '../../utils';
import ApiHelper from '../ApiHelper';
import { AuthState } from '../auth/Types';
import * as Actions from './Actions';
import { CustomerError, CustomerErrorInfo, CustomerState } from './Types';

type RootSubState = {
    authState: AuthState,
    customerState: CustomerState,
};

type CommonAppThunkAction<R> = ThunkAction<R, RootSubState, undefined, Action>;
type CommonAppThunkDispatch = ThunkDispatch<RootSubState, undefined, Action>;

export type ServiceActions = {
    customerData: () => Promise<CustomerDataDTO>,
    vehicleData: (Vehcode: number) => Promise<VehicleDataDTO>,
    customerCards: () => Promise<CardDetailsDTO[]>,
    vehicleInvoices: (vehcode: number) => Promise<InvoiceGroupDTO[]>,
    vehicleMaintenanceContracts: (vehcode: number) => Promise<MaintenanceContractChartResultDTO>,
    // vehicleMaintenanceContractsByPlateOrVin: (licensePlate: string, vin: string) => Promise<MaintenanceContractChartResultDTO>,
    vehicleMaintenanceContractUpgrades: (vehcode: number) => Promise<MaintenanceContractUpgradeChartResultDTO>,
    vehicleMaintenanceContractUpgradesByPlateOrVin: (licensePlate: string, vin: string) => Promise<MaintenanceContractUpgradeChartResultDTO>,
    vehicleMaintenanceContractPDF: (vehcode: number) => Promise<Blob>,
    vehicleSRPMM: (search: number, dealerCode: number, srpType: string) => Promise<SRPMMChartResultDTO>,
    vehicleSRPMMByPlateOrVin: (dealerCode: number, licensePlate: string, vin: string, srpType: string) => Promise<SRPMMChartResultDTO>,
    vehicleAllVisits: (vehcode: number) => Promise<VehicleHistoryDTO>,
    changePassword: (changePasswordDTO: ChangePasswordDTO) => Promise<void>,
    // deleteAccount: () => Promise<void>,
    updateCustomer: (updateCustomerDTO: UpdateCustomerDTO) => Promise<void>,
    updateCustomerForSubscription: (updateCustomerForSubscriptionDTO: UpdateCustomerForSubscriptionDTO) => Promise<void>,
    updateNif: (nif: string) => Promise<void>,
    addVehicle: (addVehicleDTO: AddVehicleDTO) => Promise<boolean>,
    deleteVehicle: (vehcode: number) => Promise<void>,
    deleteCard: (licensePlate: string) => Promise<void>,
    uploadProfilePicture: (data: BinaryFileWithContentDTO) => Promise<void>,
    uploadVehicleDocs: (data: VehicleDocsDTO) => Promise<void>,
    addValueAndCareCard: (addValueAndCareCardDTO: AddValueAndCareCardDTO) => Promise<void>,
};

export const ServiceActionPropTypes = PropTypes.shape({
    customerData: PropTypes.func.isRequired,
    vehicleData: PropTypes.func.isRequired,
    customerCards: PropTypes.func.isRequired,
    vehicleInvoices: PropTypes.func.isRequired,
    vehicleMaintenanceContracts: PropTypes.func.isRequired,
    // vehicleMaintenanceContractsByPlateOrVin: PropTypes.func.isRequired,
    vehicleMaintenanceContractUpgrades: PropTypes.func.isRequired,
    vehicleMaintenanceContractUpgradesByPlateOrVin: PropTypes.func.isRequired,
    vehicleMaintenanceContractPDF: PropTypes.func.isRequired,
    vehicleSRPMM: PropTypes.func.isRequired,
    vehicleSRPMMByPlateOrVin: PropTypes.func.isRequired,
    vehicleAllVisits: PropTypes.func.isRequired,
    changePassword: PropTypes.func.isRequired,
    // deleteAccount: PropTypes.func.isRequired,
    updateCustomer: PropTypes.func.isRequired,
    updateCustomerForSubscription: PropTypes.func.isRequired,
    updateNif: PropTypes.func.isRequired,
    addVehicle: PropTypes.func.isRequired,
    deleteVehicle: PropTypes.func.isRequired,
    deleteCard: PropTypes.func.isRequired,
    uploadProfilePicture: PropTypes.func.isRequired,
    uploadVehicleDocs: PropTypes.func.isRequired,
    addValueAndCareCard: PropTypes.func.isRequired,
});

export const createServiceActions = (config: ConfigBase) => {
    const apiHelper = ApiHelper(config);

    const customerData = (): CommonAppThunkAction<Promise<CustomerDataDTO>> => {
        return async (dispatch) => {
            try {
                dispatch(Actions.customerDataRequest());
                const axiosResponse: AxiosResponse = await dispatch(apiHelper.get(`/api/${config.ResourceCode}/users/getuserdata/`, null));
                const response = axiosResponse.data as CustomerDataDTO;
                dispatch(Actions.customerDataSuccess(response));
                return response;
            } catch (error) {
                dispatch(Actions.customerDataFailure());
                throw error;
            }
        };
    };

    const vehicleData = (vehcode: number): CommonAppThunkAction<Promise<VehicleDataDTO>> => {
        return async (dispatch) => {
            try {
                dispatch(Actions.vehicleDataRequest());
                const axiosResponse: AxiosResponse = await dispatch(apiHelper.get(`/api/${config.ResourceCode}/users/getvehicledata/?vehcode=${vehcode}`, null));
                const response = axiosResponse.data as VehicleDataDTO;
                dispatch(Actions.vehicleDataSuccess(response));
                return response;
            } catch (error) {
                dispatch(Actions.vehicleDataFailure());
                throw error;
            }
        };
    };

    const customerCards = (): CommonAppThunkAction<Promise<CardDetailsDTO[]>> => {
        return async (dispatch) => {
            try {
                dispatch(Actions.customerCardsRequest());
                const axiosResponse: AxiosResponse = await dispatch(apiHelper.get(`/api/${config.ResourceCode}/valueandcare/getcustomercards/`, null));
                const response = axiosResponse.data as CardDetailsDTO[];
                dispatch(Actions.customerCardsSuccess(response));
                return response;
            } catch (error) {
                dispatch(Actions.customerCardsFailure());
                throw error;
            }
        };
    };

    const vehicleInvoices = (vehcode: number): CommonAppThunkAction<Promise<InvoiceGroupDTO[]>> => {
        return async (dispatch) => {
            try {
                dispatch(Actions.vehicleInvoicesRequest());
                const axiosResponse: AxiosResponse = await dispatch(apiHelper.get(`/api/${config.ResourceCode}/contractsinvoices/getallinvoices/?vehcode=${vehcode}`, null));
                const response = axiosResponse.data as InvoiceGroupDTO[];
                dispatch(Actions.vehicleInvoicesSuccess(response));
                return response;
            } catch (error) {
                dispatch(Actions.vehicleInvoicesFailure());
                throw error;
            }
        };
    };

    const vehicleMaintenanceContracts = (vehcode: number): CommonAppThunkAction<Promise<MaintenanceContractChartResultDTO>> => {
        return async (dispatch) => {
            try {
                dispatch(Actions.vehicleMaintenanceContractsRequest());
                const axiosResponse: AxiosResponse = await dispatch(apiHelper.get(`/api/${config.ResourceCode}/contractsinvoices/getvehiclemaintenancecontracts/?vehcode=${vehcode}`, null));
                const response = axiosResponse.data as MaintenanceContractChartResultDTO;
                dispatch(Actions.vehicleMaintenanceContractsSuccess(response));
                return response;
            } catch (error) {
                dispatch(Actions.vehicleMaintenanceContractsFailure());
                throw error;
            }
        };
    };

    /*
    const vehicleMaintenanceContractsByPlateOrVin = (licensePlate: string, vin: string): CommonAppThunkAction<Promise<MaintenanceContractChartResultDTO>> => {
        return async (dispatch) => {
            try {
                dispatch(Actions.vehicleMaintenanceContractsRequest());
                const axiosResponse: AxiosResponse = await dispatch(apiHelper.get(`/api/${config.ResourceCode}/contractsinvoices/getvehiclemaintenancecontractsbyplateorvin/?vin=${vin}&licensePlate=${licensePlate}`, null));
                const response = axiosResponse.data as MaintenanceContractChartResultDTO;
                dispatch(Actions.vehicleMaintenanceContractsSuccess(response));
                return response;
            } catch (error) {
                dispatch(Actions.vehicleMaintenanceContractsFailure());
                throw error;
            }
        };
    };
    */

    const vehicleMaintenanceContractUpgrades = (vehcode: number): CommonAppThunkAction<Promise<MaintenanceContractUpgradeChartResultDTO>> => {
        return async (dispatch) => {
            try {
                dispatch(Actions.vehicleMaintenanceContractUpgradesRequest());
                const axiosResponse: AxiosResponse = await dispatch(apiHelper.get(`/api/${config.ResourceCode}/contractsinvoices/getvehiclemaintenancecontractsupgrade/?vehcode=${vehcode}`, null));
                const response = axiosResponse.data as MaintenanceContractUpgradeChartResultDTO;
                dispatch(Actions.vehicleMaintenanceContractUpgradesSuccess(response));
                return response;
            } catch (error) {
                dispatch(Actions.vehicleMaintenanceContractUpgradesFailure());
                throw error;
            }
        };
    };

    const vehicleMaintenanceContractUpgradesByPlateOrVin = (licensePlate: string, vin: string): CommonAppThunkAction<Promise<MaintenanceContractUpgradeChartResultDTO>> => {
        return async (dispatch) => {
            try {
                dispatch(Actions.vehicleMaintenanceContractUpgradesRequest());
                const axiosResponse: AxiosResponse = await dispatch(apiHelper.get(`/api/${config.ResourceCode}/contractsinvoices/getvehiclemaintenancecontractsupgradebyplateorvin/?vin=${vin}&licensePlate=${licensePlate}`, null));
                const response = axiosResponse.data as MaintenanceContractUpgradeChartResultDTO;
                dispatch(Actions.vehicleMaintenanceContractUpgradesSuccess(response));
                return response;
            } catch (error) {
                dispatch(Actions.vehicleMaintenanceContractUpgradesFailure());
                throw error;
            }
        };
    };

    const vehicleMaintenanceContractPDF = (vehcode: number): CommonAppThunkAction<Promise<Blob>> => {
        return async (dispatch) => {
            try {
                dispatch(Actions.vehicleMaintenanceContractPDFRequest());
                const axiosResponse: AxiosResponse = await dispatch(apiHelper.get(`/api/${config.ResourceCode}/contractsinvoices/getvehiclemaintenancecontractspdf/?vehcode=${vehcode}`, null));
                const response = axiosResponse.data as Blob;
                dispatch(Actions.vehicleMaintenanceContractPDFSuccess(response));
                return response;
            } catch (error) {
                dispatch(Actions.vehicleMaintenanceContractPDFFailure());
                throw error;
            }
        };
    };

    const vehicleSRPMM = (vehcode: number, dealerCode: number, srpType: string): CommonAppThunkAction<Promise<SRPMMChartResultDTO>> => {
        return async (dispatch) => {
            try {
                dispatch(Actions.vehicleSRPMMRequest());
                const axiosResponse: AxiosResponse = await dispatch(apiHelper.get(`/api/${config.ResourceCode}/valueandcare/getVehicleSRPMMByVehcode/?vehcode=${vehcode}&dealerCode=${dealerCode}&srpType=${srpType}`, null));
                const response = axiosResponse.data as SRPMMChartResultDTO;
                dispatch(Actions.vehicleSRPMMSuccess(response));
                return response;
            } catch (error) {
                dispatch(Actions.vehicleSRPMMFailure());
                throw error;
            }
        };
    };

    const vehicleSRPMMByPlateOrVin = (dealerCode: number, licensePlate: string, vin: string, srpType: string): CommonAppThunkAction<Promise<SRPMMChartResultDTO>> => {
        return async (dispatch) => {
            try {
                dispatch(Actions.vehicleSRPMMRequest());
                const axiosResponse: AxiosResponse = await dispatch(apiHelper.get(`/api/${config.ResourceCode}/valueandcare/getVehicleSRPMMByVehcodeByPlateOrVin/?dealerCode=${dealerCode}&vin=${vin}&licensePlate=${licensePlate}&srpType=${srpType}`, null));
                const response = axiosResponse.data as SRPMMChartResultDTO;
                dispatch(Actions.vehicleSRPMMSuccess(response));
                return response;
            } catch (error) {
                dispatch(Actions.vehicleSRPMMFailure());
                throw error;
            }
        };
    };

    const vehicleAllVisits = (vehcode: number): CommonAppThunkAction<Promise<VehicleHistoryDTO>> => {
        return async (dispatch) => {
            try {
                dispatch(Actions.vehicleAllVisitsRequest());
                const axiosResponse: AxiosResponse = await dispatch(apiHelper.get(`/api/${config.ResourceCode}/contractsinvoices/getVehicleAllVisitsByVehcode/?vehcode=${vehcode}`, null));
                const response = axiosResponse.data as VehicleHistoryDTO;
                dispatch(Actions.vehicleAllVisitsSuccess(response));
                return response;
            } catch (error) {
                dispatch(Actions.vehicleAllVisitsFailure());
                throw error;
            }
        };
    };

    const changePassword = (changePasswordDTO: ChangePasswordDTO): CommonAppThunkAction<Promise<void>> => {
        return async (dispatch) => {
            try {
                dispatch(Actions.changePasswordRequest());
                const response: AxiosResponse = await dispatch(apiHelper.post(`/api/${config.ResourceCode}/users/changepassword/`, changePasswordDTO));
                Utils.checkHttpStatus(response);
                dispatch(Actions.changePasswordSuccess());
            } catch (error) {
                dispatch(apiHelper.logFrontendError(error, true));
                try {
                    const anyError = error as any;
                    const customerErrorInfo: CustomerErrorInfo = {
                        response: anyError?.response,
                        errorCode: config.Constants.ErrorCodes.UnknownError,
                        errorMessage: null,
                        exceptionType: null,
                    };

                    customerErrorInfo.errorCode = config.Constants.ErrorCodes.UnknownError;
                    if (Axios.isAxiosError(error) && error.response) {
                        customerErrorInfo.errorMessage = `${error.response.status} ${error.response.statusText}`;
                        customerErrorInfo.exceptionType = error.response.data.ExceptionType;
                    } else {
                        customerErrorInfo.errorMessage = anyError?.message;
                    }
                    dispatch(Actions.changePasswordFailure(customerErrorInfo));
                    throw new CustomerError(customerErrorInfo);
                } catch (subError) {
                    if (subError instanceof CustomerError) {
                        throw subError;
                    } else {
                        throw error;
                    }
                }
            }
        };
    };

    /*
    const deleteAccount = (): CommonAppThunkAction<Promise<void>> => {
        return async (dispatch) => {
            try {
                dispatch(Actions.deleteAccountRequest());
                const response: AxiosResponse = await dispatch(apiHelper.del(`/api/${config.ResourceCode}/users/deleteaccount/`, null));
                Utils.checkHttpStatus(response);
                dispatch(Actions.deleteAccountSuccess());
            } catch (error) {
                dispatch(apiHelper.logFrontendError(error));
                try {
                    const customerErrorInfo: CustomerErrorInfo = {
                        response: error.response,
                        errorCode: config.Constants.ErrorCodes.UnknownError,
                        errorMessage: null,
                        exceptionType: null,
                    };

                    customerErrorInfo.errorCode = config.Constants.ErrorCodes.UnknownError;
                    if ((error as AxiosError).response) {
                        customerErrorInfo.errorMessage = `${error.response.status} ${error.response.statusText}`;
                        customerErrorInfo.exceptionType = error.response.data.ExceptionType;
                    } else {
                        customerErrorInfo.errorMessage = error.message;
                    }
                    dispatch(Actions.deleteAccountFailure(customerErrorInfo));
                    throw new CustomerError(customerErrorInfo);
                } catch (subError) {
                    if (subError instanceof CustomerError) {
                        throw subError;
                    } else {
                        throw error;
                    }
                }
            }
        };
    };
    */

    const updateCustomer = (updateCustomerDTO: UpdateCustomerDTO): CommonAppThunkAction<Promise<void>> => {
        return async (dispatch) => {
            try {
                dispatch(Actions.updateCustomerRequest());
                const response: AxiosResponse = await dispatch(apiHelper.post(`/api/${config.ResourceCode}/users/updatecustomer/`, updateCustomerDTO));
                Utils.checkHttpStatus(response);
                dispatch(Actions.updateCustomerSuccess());
            } catch (error) {
                dispatch(apiHelper.logFrontendError(error));
                try {
                    const anyError = error as any;
                    const customerErrorInfo: CustomerErrorInfo = {
                        response: anyError?.response,
                        errorCode: config.Constants.ErrorCodes.UnknownError,
                        errorMessage: null,
                        exceptionType: null,
                    };

                    customerErrorInfo.errorCode = config.Constants.ErrorCodes.UnknownError;
                    if (Axios.isAxiosError(error) && error.response) {
                        customerErrorInfo.errorMessage = `${error.response.status} ${error.response.statusText}`;
                        customerErrorInfo.exceptionType = error.response.data.ExceptionType;
                    } else {
                        customerErrorInfo.errorMessage = anyError?.message;
                    }
                    dispatch(Actions.updateCustomerFailure(customerErrorInfo));
                    throw new CustomerError(customerErrorInfo);
                } catch (subError) {
                    if (subError instanceof CustomerError) {
                        throw subError;
                    } else {
                        throw error;
                    }
                }
            }
        };
    };

    const updateCustomerForSubscription = (updateCustomerForSubscriptionDTO: UpdateCustomerForSubscriptionDTO): CommonAppThunkAction<Promise<void>> => {
        return async (dispatch) => {
            try {
                dispatch(Actions.updateCustomerForSubscriptionRequest());
                const response: AxiosResponse = await dispatch(apiHelper.post(`/api/${config.ResourceCode}/users/updateCustomerForSubscription/`, updateCustomerForSubscriptionDTO));
                Utils.checkHttpStatus(response);
                dispatch(Actions.updateCustomerForSubscriptionSuccess());
            } catch (error) {
                dispatch(apiHelper.logFrontendError(error));
                try {
                    const anyError = error as any;
                    const customerErrorInfo: CustomerErrorInfo = {
                        response: anyError?.response,
                        errorCode: config.Constants.ErrorCodes.UnknownError,
                        errorMessage: null,
                        exceptionType: null,
                    };

                    customerErrorInfo.errorCode = config.Constants.ErrorCodes.UnknownError;
                    if (Axios.isAxiosError(error) && error.response) {
                        customerErrorInfo.errorMessage = `${error.response.status} ${error.response.statusText}`;
                        customerErrorInfo.exceptionType = error.response.data.ExceptionType;
                    } else {
                        customerErrorInfo.errorMessage = anyError?.message;
                    }
                    dispatch(Actions.updateCustomerForSubscriptionFailure(customerErrorInfo));
                    throw new CustomerError(customerErrorInfo);
                } catch (subError) {
                    if (subError instanceof CustomerError) {
                        throw subError;
                    } else {
                        throw error;
                    }
                }
            }
        };
    };

    const updateNif = (nif: string): CommonAppThunkAction<Promise<void>> => {
        return async (dispatch) => {
            try {
                dispatch(Actions.updateNifRequest());
                const response: AxiosResponse = await dispatch(apiHelper.post(`/api/${config.ResourceCode}/users/updatenif?nif=${nif}`, null));
                Utils.checkHttpStatus(response);
                dispatch(Actions.updateNifSuccess());
            } catch (error) {
                dispatch(apiHelper.logFrontendError(error));
                try {
                    const anyError = error as any;
                    const customerErrorInfo: CustomerErrorInfo = {
                        response: anyError?.response,
                        errorCode: config.Constants.ErrorCodes.UnknownError,
                        errorMessage: null,
                        exceptionType: null,
                    };

                    customerErrorInfo.errorCode = config.Constants.ErrorCodes.UnknownError;
                    if (Axios.isAxiosError(error) && error.response) {
                        customerErrorInfo.errorMessage = `${error.response.status} ${error.response.statusText}`;
                        customerErrorInfo.exceptionType = error.response.data.ExceptionType;
                    } else {
                        customerErrorInfo.errorMessage = anyError?.message;
                    }
                    dispatch(Actions.updateNifFailure(customerErrorInfo));
                    throw new CustomerError(customerErrorInfo);
                } catch (subError) {
                    if (subError instanceof CustomerError) {
                        throw subError;
                    } else {
                        throw error;
                    }
                }
            }
        };
    };

    const addVehicle = (addVehicleDTO: AddVehicleDTO): CommonAppThunkAction<Promise<boolean>> => {
        return async (dispatch) => {
            try {
                dispatch(Actions.addVehicleRequest());
                const axiosResponse: AxiosResponse = await dispatch(apiHelper.post(`/api/${config.ResourceCode}/valueandcare/addvehicle/`, addVehicleDTO));
                Utils.checkHttpStatus(axiosResponse);
                const response = axiosResponse.data as boolean;
                dispatch(Actions.addVehicleSuccess());
                return response;
            } catch (error) {
                dispatch(apiHelper.logFrontendError(error));
                try {
                    const anyError = error as any;
                    const customerErrorInfo: CustomerErrorInfo = {
                        response: anyError?.response,
                        errorCode: config.Constants.ErrorCodes.UnknownError,
                        errorMessage: null,
                        exceptionType: null,
                    };

                    customerErrorInfo.errorCode = config.Constants.ErrorCodes.UnknownError;
                    if (Axios.isAxiosError(error) && error.response) {
                        customerErrorInfo.errorMessage = `${error.response.status} ${error.response.statusText}`;
                        customerErrorInfo.exceptionType = error.response.data.ExceptionType;
                    } else {
                        customerErrorInfo.errorMessage = anyError?.message;
                    }
                    dispatch(Actions.addVehicleFailure(customerErrorInfo));
                    throw new CustomerError(customerErrorInfo);
                } catch (subError) {
                    if (subError instanceof CustomerError) {
                        throw subError;
                    } else {
                        throw error;
                    }
                }
            }
        };
    };

    const deleteVehicle = (vehcode: number): CommonAppThunkAction<Promise<void>> => {
        return async (dispatch) => {
            try {
                dispatch(Actions.deleteVehicleRequest());
                const response: AxiosResponse = await dispatch(apiHelper.del(`/api/${config.ResourceCode}/valueandcare/deletevehicle/?vehcode=${vehcode}`, null));
                Utils.checkHttpStatus(response);
                dispatch(Actions.deleteVehicleSuccess());
            } catch (error) {
                dispatch(apiHelper.logFrontendError(error));
                try {
                    const anyError = error as any;
                    const customerErrorInfo: CustomerErrorInfo = {
                        response: anyError?.response,
                        errorCode: config.Constants.ErrorCodes.UnknownError,
                        errorMessage: null,
                        exceptionType: null,
                    };

                    customerErrorInfo.errorCode = config.Constants.ErrorCodes.UnknownError;
                    if (Axios.isAxiosError(error) && error.response) {
                        customerErrorInfo.errorMessage = `${error.response.status} ${error.response.statusText}`;
                        customerErrorInfo.exceptionType = error.response.data.ExceptionType;
                    } else {
                        customerErrorInfo.errorMessage = anyError?.message;
                    }
                    dispatch(Actions.deleteVehicleFailure(customerErrorInfo));
                    throw new CustomerError(customerErrorInfo);
                } catch (subError) {
                    if (subError instanceof CustomerError) {
                        throw subError;
                    } else {
                        throw error;
                    }
                }
            }
        };
    };

    const deleteCard = (licensePlate: string): CommonAppThunkAction<Promise<void>> => {
        return async (dispatch) => {
            try {
                dispatch(Actions.deleteCardRequest());
                const response: AxiosResponse = await dispatch(apiHelper.del(`/api/${config.ResourceCode}/valueandcare/deletecard/?licensePlate=${licensePlate}`, null));
                Utils.checkHttpStatus(response);
                dispatch(Actions.deleteCardSuccess());
            } catch (error) {
                dispatch(apiHelper.logFrontendError(error));
                try {
                    const anyError = error as any;
                    const customerErrorInfo: CustomerErrorInfo = {
                        response: anyError?.response,
                        errorCode: config.Constants.ErrorCodes.UnknownError,
                        errorMessage: null,
                        exceptionType: null,
                    };

                    customerErrorInfo.errorCode = config.Constants.ErrorCodes.UnknownError;
                    if (Axios.isAxiosError(error) && error.response) {
                        customerErrorInfo.errorMessage = `${error.response.status} ${error.response.statusText}`;
                        customerErrorInfo.exceptionType = error.response.data.ExceptionType;
                    } else {
                        customerErrorInfo.errorMessage = anyError?.message;
                    }
                    dispatch(Actions.deleteCardFailure(customerErrorInfo));
                    throw new CustomerError(customerErrorInfo);
                } catch (subError) {
                    if (subError instanceof CustomerError) {
                        throw subError;
                    } else {
                        throw error;
                    }
                }
            }
        };
    };

    const uploadProfilePicture = (data: BinaryFileWithContentDTO): CommonAppThunkAction<Promise<void>> => {
        return async (dispatch) => {
            try {
                dispatch(Actions.uploadProfilePictureRequest());
                const formData = new FormData();
                formData.append('fileName', data.fileName || 'unknownFileName');
                formData.append('fileExt', data.fileExt || 'unknownFileExtension');
                formData.append('content', data.content ? data.content : '');
                await dispatch(apiHelper.post(`/api/${config.ResourceCode}/valueandcare/uploadprofilepicture/`, formData, null));
                dispatch(Actions.uploadProfilePictureSuccess());
            } catch (error) {
                dispatch(apiHelper.logFrontendError(error));
                try {
                    const anyError = error as any;
                    const customerErrorInfo: CustomerErrorInfo = {
                        response: anyError?.response,
                        errorCode: config.Constants.ErrorCodes.UnknownError,
                        errorMessage: null,
                        exceptionType: null,
                    };

                    customerErrorInfo.errorCode = config.Constants.ErrorCodes.UnknownError;
                    if (Axios.isAxiosError(error) && error.response) {
                        customerErrorInfo.errorMessage = `${error.response.status} ${error.response.statusText}`;
                        customerErrorInfo.exceptionType = error.response.data.ExceptionType;
                    } else {
                        customerErrorInfo.errorMessage = anyError?.message;
                    }
                    dispatch(Actions.uploadProfilePictureFailure(customerErrorInfo));
                    throw new CustomerError(customerErrorInfo);
                } catch (subError) {
                    if (subError instanceof CustomerError) {
                        throw subError;
                    } else {
                        throw error;
                    }
                }
            }
        };
    };

    const uploadVehicleDocs = (data: VehicleDocsDTO): CommonAppThunkAction<Promise<void>> => {
        return async (dispatch) => {
            try {
                dispatch(Actions.uploadVehicleDocsRequest());
                await dispatch(apiHelper.post(`/api/${config.ResourceCode}/valueandcare/uploadvehicledocs/`, data, null));
            } catch (error) {
                dispatch(apiHelper.logFrontendError(error));
                try {
                    const anyError = error as any;
                    const customerErrorInfo: CustomerErrorInfo = {
                        response: anyError?.response,
                        errorCode: config.Constants.ErrorCodes.UnknownError,
                        errorMessage: null,
                        exceptionType: null,
                    };

                    customerErrorInfo.errorCode = config.Constants.ErrorCodes.UnknownError;
                    if (Axios.isAxiosError(error) && error.response) {
                        customerErrorInfo.errorMessage = `${error.response.status} ${error.response.statusText}`;
                        customerErrorInfo.exceptionType = error.response.data.ExceptionType;
                    } else {
                        customerErrorInfo.errorMessage = anyError?.message;
                    }
                    dispatch(Actions.uploadVehicleDocsFailure(customerErrorInfo));
                    throw new CustomerError(customerErrorInfo);
                } catch (subError) {
                    if (subError instanceof CustomerError) {
                        throw subError;
                    } else {
                        throw error;
                    }
                }
            }
        };
    };

    const addValueAndCareCard = (addValueAndCareCardDTO: AddValueAndCareCardDTO): CommonAppThunkAction<Promise<void>> => {
        return async (dispatch) => {
            try {
                dispatch(Actions.addValueAndCareCardRequest());
                const response: AxiosResponse = await dispatch(apiHelper.post(`/api/${config.ResourceCode}/valueandcare/addvalueandcarecard/`, addValueAndCareCardDTO));
                Utils.checkHttpStatus(response);
                dispatch(Actions.addValueAndCareCardSuccess());
            } catch (error) {
                dispatch(apiHelper.logFrontendError(error));
                try {
                    const anyError = error as any;
                    const customerErrorInfo: CustomerErrorInfo = {
                        response: anyError?.response,
                        errorCode: config.Constants.ErrorCodes.UnknownError,
                        errorMessage: null,
                        exceptionType: null,
                    };

                    customerErrorInfo.errorCode = config.Constants.ErrorCodes.UnknownError;
                    if (Axios.isAxiosError(error) && error.response) {
                        customerErrorInfo.errorMessage = `${error.response.status} ${error.response.statusText}`;
                        customerErrorInfo.exceptionType = error.response.data.ExceptionType;
                    } else {
                        customerErrorInfo.errorMessage = anyError?.message;
                    }
                    dispatch(Actions.addValueAndCareCardFailure(customerErrorInfo));
                    throw new CustomerError(customerErrorInfo);
                } catch (subError) {
                    if (subError instanceof CustomerError) {
                        throw subError;
                    } else {
                        throw error;
                    }
                }
            }
        };
    };

    return {
        customerData,
        vehicleData,
        customerCards,
        vehicleInvoices,
        vehicleMaintenanceContracts,
        // vehicleMaintenanceContractsByPlateOrVin,
        vehicleMaintenanceContractUpgrades,
        vehicleMaintenanceContractUpgradesByPlateOrVin,
        vehicleMaintenanceContractPDF,
        vehicleSRPMM,
        vehicleSRPMMByPlateOrVin,
        vehicleAllVisits,
        changePassword,
        // deleteAccount,
        updateCustomer,
        updateCustomerForSubscription,
        updateNif,
        addVehicle,
        deleteVehicle,
        deleteCard,
        uploadProfilePicture,
        uploadVehicleDocs,
        addValueAndCareCard,
    };
};

export const bindServiceActionCreators = (config: ConfigBase, dispatch: CommonAppThunkDispatch): ServiceActions => {
    const services = createServiceActions(config);
    return {
        customerData: () => dispatch(services.customerData()),
        vehicleData: (vehcode: number) => dispatch(services.vehicleData(vehcode)),
        customerCards: () => dispatch(services.customerCards()),
        vehicleInvoices: (vehcode: number) => dispatch(services.vehicleInvoices(vehcode)),
        vehicleMaintenanceContracts: (vehcode: number) => dispatch(services.vehicleMaintenanceContracts(vehcode)),
        // vehicleMaintenanceContractsByPlateOrVin: (licensePlate: string, vin: string) => dispatch(services.vehicleMaintenanceContractsByPlateOrVin(licensePlate, vin)),
        vehicleMaintenanceContractUpgrades: (vehcode: number) => dispatch(services.vehicleMaintenanceContractUpgrades(vehcode)),
        vehicleMaintenanceContractUpgradesByPlateOrVin: (licensePlate: string, vin: string) => dispatch(services.vehicleMaintenanceContractUpgradesByPlateOrVin(licensePlate, vin)),
        vehicleMaintenanceContractPDF: (vehcode: number) => dispatch(services.vehicleMaintenanceContractPDF(vehcode)),
        vehicleSRPMM: (vehcode: number, dealerCode: number, srpType: string) => dispatch(services.vehicleSRPMM(vehcode, dealerCode, srpType)),
        vehicleSRPMMByPlateOrVin: (dealerCode: number, licensePlate: string, vin: string, srpType: string) => dispatch(services.vehicleSRPMMByPlateOrVin(dealerCode, licensePlate, vin, srpType)),
        vehicleAllVisits: (vehcode: number) => dispatch(services.vehicleAllVisits(vehcode)),
        changePassword: (changePasswordDTO: ChangePasswordDTO) => dispatch(services.changePassword(changePasswordDTO)),
        // deleteAccount: () => dispatch(services.deleteAccount()),
        updateCustomer: (updateCustomerDTO: UpdateCustomerDTO) => dispatch(services.updateCustomer(updateCustomerDTO)),
        updateCustomerForSubscription: (updateCustomerForSubscriptionDTO: UpdateCustomerForSubscriptionDTO) => dispatch(services.updateCustomerForSubscription(updateCustomerForSubscriptionDTO)),
        updateNif: (nif: string) => dispatch(services.updateNif(nif)),
        addVehicle: (addVehicleDTO: AddVehicleDTO) => dispatch(services.addVehicle(addVehicleDTO)),
        deleteVehicle: (vehcode: number) => dispatch(services.deleteVehicle(vehcode)),
        deleteCard: (licensePlate: string) => dispatch(services.deleteCard(licensePlate)),
        uploadProfilePicture: (data: BinaryFileWithContentDTO) => dispatch(services.uploadProfilePicture(data)),
        uploadVehicleDocs: (data: VehicleDocsDTO) => dispatch(services.uploadVehicleDocs(data)),
        addValueAndCareCard: (addValueAndCareCardDTO: AddValueAndCareCardDTO) => dispatch(services.addValueAndCareCard(addValueAndCareCardDTO)),
    };
};
